<template>
  <div class="FileDropzoneWrapper">
    <div 
      v-if="uploading"
      class="FileDropzone d-flex flex-column justify-content-center">
      <div>
        Bezig met uploaden...
      </div>
    </div>
    <div 
      v-else
      class="FileDropzone"
      :class="{'FileDropzone--active': dragging}">
      <input
        :id="`f-${category}`"
        class="FileDropzone__input"
        type="file"
        :name="`f-${category}`"
        multiple
        :accept="allowedTypes"
        @change="handleFileInput"
        onclick="this.value=null;"
      >
      <label
        class="FileDropzone__label FileDropzone-label"
        :for="`f-${category}`"
        @drop="drop"
        @dragover="dragover"
        @dragenter="dragenter"
        @dragleave="dragleave"
      >
        <span
          v-if="!activeDragFiles"
          class="d-flex flex-column"
        >
          <span class="FileDropzone-label__text">Sleep de bestanden in dit vlak of</span>
          <span>
            <span class="textLink">selecteer bestanden</span>
            <b-icon icon="download" />
          </span>
        </span>
        <span v-else>{{ activeDragFiles }} bestanden geselecteerd</span>
      </label>
    </div>

    <AllowedFileTypes :allowedTypes="allowedTypes" />
  </div>
</template>

<script>

import AllowedFileTypes from '@/components/files/AllowedFileTypes.vue'

import { uploadFile } from '@/services/aws.js'
import { mapMutations, mapGetters } from 'vuex'

export default {
  name: 'FileDropzone',
  components: {
    AllowedFileTypes
  },
  props: {
    uuid: {
      type: String,
      required: true
    },
    allowedTypes: {
      type: Array,
      default: function() {
        return ['image/jpg', 'image/jpeg', 'image/png']
      }
    },
    category: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      dragging: false,
      activeDragFiles: null,
      dragEnterTarget: null,
      counter: 0,
      uploading: false
    }
  },
  computed: {
    ...mapGetters('files', ['getFileByUuid'])
  },
  methods: {
    ...mapMutations('realisation', [
      'replaceRecord',
    ]),
    ...mapMutations('files', [
      'setFileByUuid',
    ]),
    async addFile({ file }) {
      console.log("ADD FILE")

      // Check file type
      if (! this.allowedTypes.includes(file.type)) {
        // TODO: ERROR MSG

        this.$notify({
          error: true,
          title: 'Upload mislukt',
          message: `Bestand van type "${file.type}" is niet toegestaan.`
        })
        return false
      }

      // Get file extension from mime type
      const extension = file.type.split('/').reverse()[0].toLowerCase()

      const filedetails = await this.$cbapi.files.getProcessUploadLink({
        processUuid: this.uuid, // the record uuid
        category: this.category,
        filename: file.name,
        extension
      })

      if (! filedetails) {
        // TODO: ERROR MSG
        this.$notify({
          error: true,
          title: 'Upload mislukt',
          message: `Het aanmaken van een upload link is mislukt.`
        })
        return false
      }

      if (filedetails?.data?.file) {
        this.setFileByUuid({ file: filedetails?.data?.file })
      }

      const uploadURL = filedetails?.data?.link // response.uploadURL
      const response = await uploadFile({ file, uploadURL })

      if (! response.ok) {
        // TODO: ERROR MSG
        this.$notify({
          error: true,
          title: 'Upload mislukt',
          message: `Het uploaden van het bestand is mislukt.`
        })
        return false
      }

      const FileInformation = this.getFileByUuid({ uuid: filedetails.data.file.uuid })

      this.$notify({
        success: true,
        title: 'Upload geslaagd',
        message: `Het bestand "${FileInformation.getFullFilename()}" is geupload. Na verificatie verschijnt het bestand.`
      })

      return true
    },

    async handleUploadFiles({ files }) {
      if (! files || ! files.length) {
        console.log("Only accept a list of files")
        return
      }

      for (let i = 0; files.length > i; i++) {
        await this.addFile({ file: files[i] })
      }

      await this.refreshRecord()
    },

    async refreshRecord() {

      // Get fresh copy of the record
      const response = await this.$cbapi.process.getByUuid({
        uuid: this.uuid
      })

      if (! response) {
        this.$notify({
          error: true,
          title: 'Synchronisatie mislukt',
          message: `Het verversen van de realisatieproces data is mislukt.`
        })
        // this.feedback = {
        //   show: true,
        //   variant: 'danger',
        //   message: 'De status wijziging is opgeslagen, maar het verversen van het record is mislukt',
        // }
        // this.busy = false
        return false
      }

      this.replaceRecord({
        record: response?.data,
      })

      console.log("Refresh Record")
    },

    /**
     * Dialog upload
     */
    async handleFileInput(event) {
      if (this.uploading === true) return

      this.uploading = true
      console.log(event, event?.target?.files)

      await this.handleUploadFiles({
        files: event?.target?.files || false
      })

      this.uploading = false
    },

    /**
     * Drag / Drop event handlers
     */
    async drop(event) {
      
      event.preventDefault()

      if (this.uploading === true) return
      this.uploading = true

      this.dragging = false
      this.activeDragFiles = null

      await this.handleUploadFiles({
        files: event?.dataTransfer?.files || false
      })

      this.uploading = false
    },
    dragenter(event) {
      event.preventDefault()

      if (! this.dragEnterTarget) {
        this.dragging = true
        this.dragEnterTarget = event.target
        this.activeDragFiles = event.dataTransfer.items.length
      } else if (event.srcElement !== this.dragEnterTarget) {
        this.counter = 1
      }
    },
    dragover(event){
      event.preventDefault()
    },
    dragleave(event) {
      event.preventDefault()

      if (! this.counter && event.srcElement === this.dragEnterTarget) {
        this.dragEnterTarget = null
        this.dragging = false
        this.activeDragFiles = 0
      } else {
        this.counter = 0
      }
    },

  }
}
</script>

<style lang="scss">
.FileDropzone {
  background-color: $neutral-grey1;
  text-align: center;
  margin-bottom: 10px;
  height: 100px;

  .FileDropzone__input {
    opacity: 0;
    position: absolute;
    z-index: -1;

    &:focus,
    &:active {
      + .FileDropzone__label {
        outline: 2px solid $primary-black;
      }
    }
  }

  .FileDropzone__label {
    display: flex;
    /* padding: 23px; */
    height: 100%;
    cursor: pointer;
    justify-content: center;
    align-items: center;

    > span {
      display: inline-flex;
      align-items: center;

      > * {
        margin-left: 5px;
      }
    }

    .b-icon {
      margin-left: 0.3rem;
      fill: $primary-darkblue;
    }
  }

  .FileDropzone-label {
    @include breakpoint('s') {
      .FileDropzone-label__text {
        display: none;
      }
    }
  }

  &.FileDropzone--active {
    background-color: $neutral-grey2;
  }

  &:hover {
    .textLink {
      color: $primary-red;
    }

    .b-icon {
      fill: $primary-red;
    }
  }
  .textLink {
    color: $primary-darkblue;
    text-decoration: underline;
  }
}
</style>